import {AbstractApi} from '../abstract-api';
import {GeneralUtility} from '../../utilities/general-utility';
import {PluginFilter, PluginSettings} from '../../plugins/abstract-data-api-plugin';

/**
 * Base for data API endpoints.
 *
 * @author Philipp Seiler <sep@earlybird.at>
 */
export abstract class DataApiEndpoint {
  protected _api: AbstractApi;

  constructor(api: AbstractApi) {
    this._api = api;
  }

  /**
   * Gets the parent API.
   *
   * @returns {AbstractApi}
   */
  protected get api(): AbstractApi {
    return this._api;
  }

  /**
   * Builds a final URL to send to an endpoint from the common and more specific endpoint parameters.
   *
   * @param {PluginSettings|null} [settings] Plugin settings
   * @param {PluginFilter|null} [filter] Filter
   * @param {string[]} [parameters] Any number of additional parameters
   * @returns {string}
   */
  protected buildUrl(
    settings: PluginSettings | null = null,
    filter: PluginFilter | null = null,
    parameters: string[] = []
  ): string {
    const commonParameters = this.buildCommonRequestParameter(settings, filter);
    const requestParameters = this.buildRequestParameter(settings, filter);
    return GeneralUtility.getUrl([...commonParameters, ...requestParameters, ...parameters]);
  }

  /**
   * Builds common request parameters for the data API.
   *
   * @param {PluginSettings|null} [settings] Plugin settings
   * @param {PluginFilter|null} [filter] Filter
   * @returns {string[]} URL parameters
   */
  protected buildCommonRequestParameter(
    settings: PluginSettings | null = null,
    filter: PluginFilter | null = null
  ): string[] {
    const parts: string[] = ['dataApi[endpoint][]=documents'];
    if (settings !== null) {
      if (settings.sorts) {
        parts.push(`dataApi[sorts]=${settings.sorts}`);
      }
      if (settings.sorts === null) {
        parts.push('dataApi[sorts]=');
      }
      if (settings.start && settings.start >= 0) {
        parts.push(`dataApi[start]=${settings.start}`);
      }
      if (settings.startingPoint && settings.startingPoint > 0) {
        parts.push(`dataApi[startingPoint]=${settings.startingPoint}`);
      }
      if (settings.itemsPerPage && settings.itemsPerPage > 0) {
        parts.push(`dataApi[rows]=${settings.itemsPerPage}`);
      }
      if (settings.limit && settings.limit > 0) {
        parts.push(`dataApi[rows]=${settings.limit}`);
      }
      if (settings.uids && settings.uids.length > 0) {
        parts.push(`dataApi[query][uids]=${settings.uids}`);
      }
      if (settings.excludeUids && settings.excludeUids.length > 0) {
        parts.push(`dataApi[query][-uids]=${settings.excludeUids}`);
      }
      if (settings.expertises && settings.expertises.length > 0) {
        parts.push(`dataApi[query][expertiseUids]=${settings.expertises}`);
      }
      if (settings.lawyers && settings.lawyers.length > 0) {
        parts.push(`dataApi[query][lawyerUids]=${settings.lawyers}`);
      }
      if (settings.positions && settings.positions.length > 0) {
        parts.push(`dataApi[query][positionUid]=${settings.positions}`);
      }
      if (settings.preserveOrder) {
        for (const field of settings.preserveOrder) {
          parts.push(`dataApi[preserveOrder][]=${field}`);
        }
      }
      if (settings.boost) {
        for (const field in settings.boost) {
          for (const key in settings.boost[field]) {
            const value = settings.boost[field][key];
            parts.push(`dataApi[boost][${field}][${key}]=${value}`);
          }
        }
      }
    }
    if (filter !== null) {
      if (filter.text) {
        parts.push(`dataApi[query][fullText]=*${filter.text}*`);
      }
      if (filter.category) {
        parts.push(`dataApi[query][categoryUids]=${filter.category}`);
      }
      if (filter.expertise) {
        parts.push(`dataApi[query][expertiseUids]=${filter.expertise}`);
      }
      if (filter.lawyer) {
        parts.push(`dataApi[query][lawyerUids]=${filter.lawyer}`);
      }
      if (filter.position) {
        parts.push(`dataApi[query][positionUid]=${filter.position}`);
      }
      if (filter.year) {
        parts.push(`dataApi[query][dateYear]=${filter.year}`);
      }
    }
    return parts;
  }

  /**
   * Builds request parameters specific for and endpoint.
   *
   * @param {PluginSettings|null} [settings] Plugin settings
   * @param {PluginFilter|null} [filter] Filter
   * @@returns {string[]} URL parameters
   */
  protected buildRequestParameter(
    settings: PluginSettings | null = null,
    filter: PluginFilter | null = null
  ): string[] {
    return [];
  }
}
